/**
 * API observability: x-device-id, traceparent headers, timing, and Faro error reporting.
 * No duplicate toasts; only adds Faro events for 5xx, network, timeout.
 */

import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { getDeviceId } from './device.service';
import { getTraceparent } from './tracing.service';
import { getObservabilityContext } from './init';
import { pushFaroEvent, reportFaroError, isObservabilityEnabled, getAppName } from './faro.config';
import type { ApiErrorType } from './types';

const START_TIME_KEY = '__observabilityStartTime';

function getPathPattern(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const path = u.pathname || '/';
    return path.replace(/\/\d+/g, '/:id').slice(0, 120);
  } catch {
    return '/unknown';
  }
}

function classifyError(error: AxiosError): ApiErrorType {
  if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
    return 'timeout';
  }
  if (!error.response) return 'network_error';
  const status = error.response.status;
  if (status === 401) return 'session_expired';
  if (status >= 500) return '5xx';
  if (status >= 400) return '4xx';
  return 'unknown';
}

function getDuration(config: InternalAxiosRequestConfig): number | undefined {
  const start = (config as unknown as Record<string, unknown>)[START_TIME_KEY];
  if (typeof start === 'number') return Math.round(Date.now() - start);
  return undefined;
}

/**
 * Request: add x-app-name, x-device-id, traceparent, and record start time.
 */
function onRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  config.headers['x-app-name'] = getAppName();
  config.headers['x-device-id'] = getDeviceId();
  const traceparent = getTraceparent();
  if (traceparent) config.headers['traceparent'] = traceparent;
  (config as unknown as Record<string, unknown>)[START_TIME_KEY] = Date.now();
  return config;
}

/**
 * Response error: report to Faro on 5xx, network_error, timeout. No toast (apiClient handles that).
 */
function onResponseError(error: AxiosError): Promise<never> {
  const config = error.config as InternalAxiosRequestConfig | undefined;
  const duration = config ? getDuration(config) : undefined;
  const path = config?.url ? getPathPattern(config.url) : '/unknown';
  const method = (config?.method ?? 'get').toUpperCase();
  const errorType = classifyError(error);

  if (isObservabilityEnabled()) {
    const status = error.response?.status;
    const { deviceId, sessionId } = getObservabilityContext();
    const attrs: Record<string, string | number> = {
      app_name: getAppName(),
      error_type: errorType,
      method,
      path,
      device_id: deviceId,
      session_id: sessionId,
    };
    if (duration != null) attrs.duration_ms = duration;
    if (status != null) attrs.status = status;
    pushFaroEvent('api_error', attrs);
    const errObj = error instanceof Error ? error : new Error(String((error as { message?: unknown }).message));
    reportFaroError(errObj, {
      app_name: getAppName(),
      error_type: errorType,
      path,
      method: String(method),
    });
  }

  return Promise.reject(error);
}

/**
 * Apply observability to the axios instance: device id, traceparent, timing, Faro error reporting.
 */
export function addObservabilityInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(onRequest, (err) => Promise.reject(err));
  instance.interceptors.response.use(
    (response) => response,
    onResponseError
  );
}
